// Job-listing trust validation. Pure-JS heuristics that flag obvious
// scam/fraud signals before the user wastes a tailored CV on a bad listing.
// Inspired by santifer/career-ops's recent "trust validation + job trust
// metadata" addition (commit ef07461). NOT a substitute for due diligence,
// but it catches the top 10 scam-tells with no AI cost.

// Phrases that strongly correlate with recruitment scams or low-effort listings.
const RED_FLAG_PHRASES = [
    { phrase: 'telegram', reason: 'Asks you to contact on Telegram — common recruitment-scam vector', severity: 'high' },
    { phrase: 'whatsapp', reason: 'Asks you to contact on WhatsApp — common recruitment-scam vector', severity: 'high' },
    { phrase: 'no experience required', reason: '"No experience required" + decent pay is a classic bait', severity: 'medium' },
    { phrase: 'no experience needed', reason: '"No experience needed" + decent pay is a classic bait', severity: 'medium' },
    { phrase: 'crypto payment', reason: 'Crypto-only payment claim', severity: 'high' },
    { phrase: 'paid in crypto', reason: 'Crypto-only payment claim', severity: 'high' },
    { phrase: 'daily payout', reason: 'Daily-payout claim is uncommon for real jobs', severity: 'medium' },
    { phrase: 'urgent hire', reason: 'Pressure-tactic language; legit roles rarely advertise as "urgent"', severity: 'low' },
    { phrase: 'start tomorrow', reason: 'Pressure-tactic language', severity: 'low' },
    { phrase: 'work from anywhere', reason: 'Vague + generic — usually means MLM or recruitment scheme', severity: 'low' },
    { phrase: 'multi-level marketing', reason: 'Explicit MLM signal', severity: 'high' },
    { phrase: 'commission only', reason: 'Pure-commission roles often have no real income', severity: 'medium' },
    { phrase: 'training fee', reason: 'Charging applicants is a scam pattern', severity: 'high' },
    { phrase: 'pay for training', reason: 'Charging applicants is a scam pattern', severity: 'high' },
    { phrase: 'send your bank', reason: 'Requesting bank info pre-offer is fraud', severity: 'high' },
    { phrase: 'send a deposit', reason: 'Requesting a deposit from a candidate is fraud', severity: 'high' },
    { phrase: 'we will send you a check', reason: 'Fake-check scam pattern', severity: 'high' },
    { phrase: '$$$', reason: 'Spammy money formatting', severity: 'low' },
    { phrase: 'click here to apply', reason: 'External "click here" links can mask phishing', severity: 'low' },
    { phrase: 'unlimited income', reason: 'Income-claim style of MLM listings', severity: 'medium' },
];

const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'protonmail.com', 'mail.com', 'live.com', 'msn.com'
]);

const VAGUE_COMPANY_NAMES = new Set(['confidential', 'private', 'company', 'employer', 'n/a', 'none', '-']);

export const scoreJobTrust = (job) => {
    const flags = [];
    let score = 100;

    const description = (job.job_description || job.description || '').toLowerCase();
    const title = (job.job_title || job.title || '').toLowerCase();
    const company = (job.employer_name || job.company || '').trim();
    const haystack = `${title}\n${description}`.toLowerCase();

    // 1. Red-flag phrase scan
    for (const rf of RED_FLAG_PHRASES) {
        if (haystack.includes(rf.phrase)) {
            const penalty = rf.severity === 'high' ? 25 : rf.severity === 'medium' ? 12 : 5;
            score -= penalty;
            flags.push({ severity: rf.severity, reason: rf.reason, evidence: rf.phrase });
        }
    }

    // 2. Missing/vague company
    if (!company) {
        score -= 20;
        flags.push({ severity: 'high', reason: 'No company name provided', evidence: '(empty)' });
    } else if (VAGUE_COMPANY_NAMES.has(company.toLowerCase()) || company.length < 2) {
        score -= 15;
        flags.push({ severity: 'medium', reason: `Vague company name: "${company}"`, evidence: company });
    }

    // 3. Missing description / too short to be real
    if (!description) {
        score -= 25;
        flags.push({ severity: 'high', reason: 'Empty job description', evidence: '(empty)' });
    } else if (description.length < 200) {
        score -= 15;
        flags.push({ severity: 'medium', reason: `Very short job description (${description.length} chars). Legit roles usually have more detail.`, evidence: `${description.length} chars` });
    }

    // 4. Free-email contact in description for what claims to be a corporate role
    const emailMatch = description.match(/[\w.+-]+@([\w-]+\.[\w.-]+)/);
    if (emailMatch && FREE_EMAIL_DOMAINS.has(emailMatch[1].toLowerCase())) {
        score -= 18;
        flags.push({
            severity: 'high',
            reason: `Contact email uses a free domain (${emailMatch[1]}) instead of a company domain`,
            evidence: emailMatch[0]
        });
    }

    // 5. ALL CAPS title — spammy listings
    if (title && title.length > 8 && title === title.toUpperCase() && /[a-z]/.test(title) === false) {
        score -= 5;
        flags.push({ severity: 'low', reason: 'ALL-CAPS job title is unusual for real listings', evidence: title });
    }

    // 6. No salary AND no location AND no company logo — combo is suspicious
    const hasSalary = !!(job.job_salary || job.salary || /\$\d/.test(description));
    const hasLocation = !!(job.job_city || job.job_country || job.location);
    const hasLogo = !!job.employer_logo;
    const lowSignalCount = [!hasSalary, !hasLocation, !hasLogo].filter(Boolean).length;
    if (lowSignalCount === 3) {
        score -= 12;
        flags.push({
            severity: 'medium',
            reason: 'No salary, location, or logo — three missing-signal markers together suggest a low-quality listing',
            evidence: 'no salary/location/logo'
        });
    }

    score = Math.max(0, Math.min(100, score));

    let level, label;
    if (score >= 80) { level = 'trusted'; label = 'Trusted'; }
    else if (score >= 55) { level = 'caution'; label = 'Caution'; }
    else { level = 'suspicious'; label = 'Suspicious'; }

    return { score, level, label, flags };
};
